import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { searchSongs } from './setlistApi';
import { countBySong } from './setlistOrder';

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

/**
 * Zoekt in de liedbibliotheek (titel, ondertitel, artiest, liednummer) en voegt een lied met één tik toe.
 * De resultaten blijven staan na het toevoegen, zodat meerdere liederen achter elkaar kunnen worden gekozen;
 * hetzelfde lied mag vaker worden toegevoegd (er is alleen een melding bij een bestaande vermelding).
 */
const SongPicker = ({ items, onAdd }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [justAdded, setJustAdded] = useState({});
  const requestRef = useRef(0);
  const inputRef = useRef(null);
  const counts = countBySong(items);

  const load = useCallback(async (text, skip) => {
    const requestId = ++requestRef.current;
    try {
      const page = await searchSongs(text.trim(), { skip, limit: PAGE_SIZE });
      if (requestId !== requestRef.current) return; // een nieuwere zoekopdracht is al onderweg
      setResults((current) => (skip ? [...current, ...page.items] : page.items));
      setHasMore(!!page.has_more);
      setStatus('ready');
    } catch (failure) {
      if (requestId !== requestRef.current) return;
      setStatus('error');
    } finally {
      if (requestId === requestRef.current) setLoadingMore(false);
    }
  }, []);

  // Zoeken terwijl de gebruiker typt (debounce); de pagina zelf laadt niet opnieuw.
  useEffect(() => {
    setStatus('loading');
    const timer = setTimeout(() => load(query, 0), query ? DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [query, load]);

  const add = async (song) => {
    setAddingId(song.id);
    try {
      await onAdd(song);
      setJustAdded((current) => ({ ...current, [song.id]: true }));
      setTimeout(() => setJustAdded((current) => { const rest = { ...current }; delete rest[song.id]; return rest; }), 2500);
    } catch (failure) { /* de aanroeper toont de foutmelding */ } finally { setAddingId(null); }
  };

  return <div className="space-y-2" data-testid="song-picker">
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
      <Input ref={inputRef} type="search" aria-label="Zoek lied op titel, artiest of nummer" placeholder="Zoek lied op titel, artiest of nummer" className="h-11 pl-9 pr-10"
        value={query} onChange={(event) => setQuery(event.target.value)} />
      {query && <button type="button" aria-label="Zoekopdracht wissen" className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded text-slate-500 hover:bg-slate-100"
        onClick={() => { setQuery(''); inputRef.current?.focus(); }}><X className="h-4 w-4" /></button>}
    </div>
    <div className="max-h-72 overflow-y-auto overscroll-contain rounded-md border border-slate-200" aria-busy={status === 'loading'}>
      {status === 'loading' && !results.length && <p className="flex items-center gap-2 p-3 text-sm text-slate-500" role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Liederen zoeken…</p>}
      {status === 'error' && <div className="flex flex-wrap items-center gap-2 p-3 text-sm text-red-700" role="alert">Zoeken is mislukt.<Button type="button" size="sm" variant="outline" onClick={() => { setStatus('loading'); load(query, 0); }}>Opnieuw proberen</Button></div>}
      {status === 'ready' && !results.length && <p className="p-3 text-sm text-slate-500" role="status">{query ? `Geen liederen gevonden voor “${query.trim()}”.` : 'De liedbibliotheek is nog leeg.'}</p>}
      {results.length > 0 && <ul aria-label="Zoekresultaten" className="divide-y divide-slate-100">
        {results.map((song) => {
          const present = counts[song.id] || 0;
          const added = justAdded[song.id];
          return <li key={song.id} className="flex items-center gap-2 p-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{song.title}</p>
              <p className="truncate text-xs text-slate-500">{[song.artist, song.subtitle, song.ccli_number && `nr. ${song.ccli_number}`, song.default_key && `toon ${song.default_key}`].filter(Boolean).join(' · ')}</p>
              {present > 0 && <p className="text-xs text-amber-700">Staat al {present === 1 ? 'in de setlist' : `${present}× in de setlist`}</p>}
            </div>
            <Button type="button" size="sm" variant={added ? 'default' : 'outline'} className={`h-10 shrink-0 ${added ? 'bg-teal-600 text-white hover:bg-teal-700' : ''}`} disabled={addingId === song.id}
              aria-label={`Voeg ${song.title} toe aan setlist${present ? ' (nogmaals)' : ''}`} onClick={() => add(song)}>
              {addingId === song.id ? <Loader2 className="h-4 w-4 animate-spin" /> : added ? <><Check className="mr-1 h-4 w-4" />Toegevoegd</> : <><Plus className="mr-1 h-4 w-4" />{present ? 'Nogmaals' : 'Toevoegen'}</>}
            </Button>
          </li>;
        })}
      </ul>}
      {hasMore && <div className="p-2"><Button type="button" variant="ghost" size="sm" className="w-full" disabled={loadingMore} onClick={() => { setLoadingMore(true); load(query, results.length); }}>{loadingMore ? 'Laden…' : 'Meer resultaten'}</Button></div>}
    </div>
  </div>;
};

export default SongPicker;
